import { ReactElement, useMemo } from 'react';

import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { OrganizationRole } from ':cloud/types/ts_types';

type PermissionRequirement = OrganizationRole | OrganizationRole[];

interface PermissionProps {
  permission: PermissionRequirement;
  fallback?: ReactElement | ReactElement[] | null;
  matchAllPermissions?: boolean;
  children: ReactElement | ReactElement[];
}

function Permission({
  permission,
  fallback = null,
  matchAllPermissions = false,
  children,
}: PermissionProps): ReactElement | null {
  const {
    user: { role },
  } = useGetUser();

  const hasPermission = useMemo(() => {
    return (requiredPermission: PermissionRequirement): boolean => {
      if (Array.isArray(requiredPermission)) {
        return matchAllPermissions
          ? requiredPermission.every((perm) => perm === role)
          : requiredPermission.some((perm) => perm === role);
      }
      return requiredPermission === role;
    };
  }, [role, matchAllPermissions]);

  if (hasPermission(permission)) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return Array.isArray(children) ? <>{children}</> : children;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return Array.isArray(fallback) ? <>{fallback}</> : fallback;
}

export default Permission;
